import * as Sentry from '@sentry/react';
import {
  FieldPasswordCompact,
  FieldTextCompact,
  Pushbutton
} from '@stratumn/atomic';
import conduentLogo from 'assets/conduent_logo_full.svg';
import to from 'await-to-js';
import { Box, Divider, OauthStratumn } from 'components/ui';
import compose from 'lodash.flowright';
import qs from 'query-string';
import type React from 'react';
import { type ChangeEvent, type FormEvent, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { SpanType, withSpanAsync } from 'tracing';
import { isBadRequestError } from 'utils/fetcher';
import { redirectAfterLogin } from 'utils/loginRedirect';
import { withRestClient } from 'wrappers';

import styles from './conduentLogin.style';

interface Client {
  loginConduent: (
    username: string,
    password: string,
    params: {
      responseType?: string | string[] | null;
      clientId?: string | string[] | null;
      redirectUri?: string | string[] | null;
      codeChallenge?: string | string[] | null;
      codeChallengeMethod?: string | string[] | null;
    }
  ) => Promise<{ redirectUri: string; authCode: string }>;
}

interface ConduentProps {
  classes: Record<string, string>;
  client: Client;
}

export const Conduent: React.FC<ConduentProps> = ({ classes, client }) => {
  const location = useLocation();
  const history = useHistory();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const togglePassword = () => setShowPassword(!showPassword);

  const isValid = () => !!username && !!password;

  const loginConduent = async (event?: FormEvent) =>
    withSpanAsync('loginConduent', SpanType.processing, async () => {
      if (event) event.preventDefault();
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      const {
        responseType,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod
      } = qs.parse(location.search);

      const [err, rsp] = await to(
        client.loginConduent(username, password, {
          responseType,
          clientId,
          redirectUri,
          codeChallenge,
          codeChallengeMethod
        })
      );

      loadingRef.current = false;
      setLoading(false);

      if (err || !rsp) {
        if (err) {
          if (isBadRequestError(err)) {
            toast.error(err.message);
            return;
          }
          Sentry.captureException(err);
          toast.error(
            `The authentication through Siebel failed.\n ${
              err instanceof Error ? err.message : 'An error occurred'
            }`
          );
        }
        return;
      }

      const { redirectUri: r, authCode } = rsp;
      redirectAfterLogin(history, r, authCode);
    });

  return (
    <>
      <div className={classes.headerLogoWrapper}>
        <img
          alt="Conduent logo"
          className={classes.headerLogo}
          src={conduentLogo}
        />
      </div>
      <Box title="Log in">
        <form
          className={classes.form}
          onSubmit={isValid() ? loginConduent : undefined}
        >
          <div className={classes.formField}>
            <FieldTextCompact
              data-testid="username"
              label="Siebel Username"
              onValueChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUsername(event.target.value)
              }
              value={username}
            />
          </div>
          <div className={classes.formField}>
            <FieldPasswordCompact
              data-testid="password"
              label="Siebel Password"
              onValueChange={(event: ChangeEvent<HTMLInputElement>) =>
                setPassword(event.target.value)
              }
              onVisibilityChange={togglePassword}
              passwordIsVisible={showPassword}
              value={password}
            />
          </div>
          <div className={classes.buttonContainer}>
            <Pushbutton
              data-testid="login-button"
              disabled={!isValid() || loading}
              primary
              type="submit"
            >
              {loading ? (
                <span className={classes.spinner} data-testid="loader" />
              ) : (
                'Log in'
              )}
            </Pushbutton>
          </div>
        </form>
        <Divider text="Or log in with" />
        <OauthStratumn />
        <div className={classes.signUp}>
          <b>Don&apos;t have an account?</b> Currently Stratumn ID is only
          available to clients using our product range. If you would like to
          learn more, <a href="mailto:hello@stratumn.com">contact us</a>.
        </div>
      </Box>
    </>
  );
};

export default compose(withRestClient, injectSheet(styles))(Conduent);
